<!--
Author: Alexey Usov (dax@xdax.ru, https://github.com/doubleaxe)
Please don't remove this comment if you use unmodified file
-->
<script setup lang="ts">
import {ref, unref, computed, onMounted, watch, nextTick} from 'vue';
import {injectBlueprintModel, type BlueprintItemModel, type RecipeIOModel} from '@/scripts/model/store';
import {mdiCursorMove, mdiTransferDown, mdiTransferUp} from '@mdi/js';
import {useElementHover, type MaybeElement} from '@vueuse/core';
import {injectSettings} from '@/scripts/settings';
import {Rect, type ReadonlyRectType} from '@/scripts/geometry';
import {SelectedClassType, screenToClient, useItemDragAndDrop, usePointAndClick} from '@/composables/drag-helpers';
import {useEventHook, useInspectorSelection, useIsMobile} from '@/composables';
import {BlueprintItemState} from '@/scripts/types';

const props = defineProps<{
    item: BlueprintItemModel;
    parent: MaybeElement;
}>();
const emit = defineEmits<{
    (e: 'recipes-menu-activate', item: BlueprintItemModel, activator: Element): void;
}>();

const settings = injectSettings();
const blueprintModel = injectBlueprintModel();
//fork addition: inspector selection — pressing a card selects it for the
//right-hand inspector; dragging still works exactly as before
const {selectedItemKey, selectItem: selectInspectorItem} = useInspectorSelection();
//fork addition (Stage I): on mobile the inspector is a full-screen
//step, so it may only open on a genuine tap — selecting on
//pointerdown (the desktop behaviour below, unchanged above 900px)
//would cover the board the instant a card drag starts. Mobile
//defers the selection to the click, gated by the same 6px tap/drag
//discrimination the toolbox and the board surface already use.
const {isMobile} = useIsMobile();
let cardPointerDownPos: {x: number; y: number} | undefined = undefined;
function onCardPointerDown(event: PointerEvent) {
    dragStart(event, props.item);
    isDragging.value = true;
    if(unref(isMobile)) {
        cardPointerDownPos = {x: event.clientX, y: event.clientY};
    } else {
        selectInspectorItem(props.item.key);
    }
}
function onCardClick(event: MouseEvent) {
    if(!unref(isMobile))
        return;
    const downPos = cardPointerDownPos;
    cardPointerDownPos = undefined;
    if(downPos && (Math.max(Math.abs(event.clientX - downPos.x), Math.abs(event.clientY - downPos.y)) > 6))
        return;
    selectInspectorItem(props.item.key);
}
const mainDivElement = ref<HTMLElement | null>(null);
//separate state, because useItemDragAndDrop is shared between all items
const isDragging = ref(false);
const itemStateColor = computed(() => settings.itemStateColor[props.item.state]);
const isHovered = useElementHover(mainDivElement);
const {hooks: itemHooks, dragStart} = useItemDragAndDrop();
const {selectItem, selectedItem} = usePointAndClick();

useEventHook([itemHooks.notifyCancel, itemHooks.notifyDrop], () => {
    isDragging.value = false;
});

const computedElevation = computed(() => {
    if(unref(isDragging))
        return 'dragging-elevation-static';
    if(unref(isHovered))
        return 'hover-elevation-static';
    return 0;
});

const computeStyle = computed(() => {
    return {
        left: `${props.item.rect.x}px`,
        top: `${props.item.rect.y}px`,
    };
});

const computedSelectedClass = computed(() => {
    if(unref(selectedItem)?.isSelected(props.item))
        return 'selected-border';
    return 'unselected-border';
});

const computedInspectorClass = computed(() => {
    if(unref(selectedItemKey) == props.item.key)
        return 'inspector-selected';
    return undefined;
});

//fork addition: while a link drag hovers this card, ring it with the
//drop verdict — green = valid target, red = invalid (drop refused),
//amber = link already exists, blue = valid after a recipe change
const computedLinkStateClass = computed(() => {
    switch(props.item.state) {
        case BlueprintItemState.CanLinkTarget: return 'link-target-valid';
        case BlueprintItemState.CannotLinkTarget: return 'link-target-invalid';
        case BlueprintItemState.LinkAlreadyExists: return 'link-target-exists';
        case BlueprintItemState.CanLinkWithRecipeChange: return 'link-target-recipe';
        default: return undefined;
    }
});

function updateIoRects() {
    nextTick(() => {
        const mainDiv = unref(mainDivElement);
        const _recipe = props.item?.selectedRecipe;
        if(!mainDiv || !_recipe)
            return;

        const mainDivrect: ReadonlyRectType = screenToClient(props.parent, mainDiv.getBoundingClientRect(), settings.scale);
        props.item.setRect(props.item.rect.assignSize(mainDivrect));
        props.item.initializationCompleted();

        const ioElements = mainDiv.querySelectorAll('[data-io-id]');
        const ioScreenRects: ReadonlyRectType[] = [];
        const ioList: RecipeIOModel[] = [];
        for(let i = 0; i < ioElements.length; i++) {
            const ioElement = ioElements.item(i);
            const key = ioElement.getAttribute('data-io-id') || '';
            const io = _recipe.itemByKey(key);
            if(io) {
                ioScreenRects.push(ioElement.getBoundingClientRect());
                ioList.push(io);
            }
        }
        const ioClientRects = screenToClient(props.parent, ioScreenRects, settings.scale);

        ioList.forEach((io, index) => { io.setRect(ioClientRects[index] || Rect.assign()); });
    });
}

const leftSide = computed(() => {
    const _recipe = props.item?.selectedRecipe;
    const io = (props.item.isFlipped ? _recipe?.visibleOutput() : _recipe?.visibleInput()) || [];
    return {
        io: () => io,
        count: io.length,
    };
});
const rightSide = computed(() => {
    const _recipe = props.item?.selectedRecipe;
    const io = (props.item.isFlipped ? _recipe?.visibleInput() : _recipe?.visibleOutput()) || [];
    return {
        io: () => io,
        count: io.length,
    };
});

//fork addition: flow orientation — LR/RL keep the side-column layout
//above (RL is the legacy mirrored state, via isFlipped); TB/BT stack
//the ports as strips along the top/bottom edges instead. The card body
//stays upright and 300px wide in every orientation; only the height
//adapts, through the same DOM measurement that already sizes cards.
const isVertical = computed(() => (props.item.orientation === 'TB') || (props.item.orientation === 'BT'));
const topSide = computed(() => {
    const _recipe = props.item?.selectedRecipe;
    const io = (props.item.orientation === 'TB' ? _recipe?.visibleInput() : _recipe?.visibleOutput()) || [];
    return {
        io: () => io,
        count: io.length,
    };
});
const bottomSide = computed(() => {
    const _recipe = props.item?.selectedRecipe;
    const io = (props.item.orientation === 'TB' ? _recipe?.visibleOutput() : _recipe?.visibleInput()) || [];
    return {
        io: () => io,
        count: io.length,
    };
});

onMounted(updateIoRects);
watch([
    () => props.item.selectedRecipe,
    () => props.item.isFlipped,
    () => props.item.orientation,
    () => props.item.linksGenerationNumber,
], updateIoRects);
watch(() => props.item.rect, (value, oldValue) => {
    //size is updated by function updateIoRects
    if((value.x !== oldValue.x) || (value.y !== oldValue.y)) {
        updateIoRects();
    }
});
</script>

<template>
    <div
        ref="mainDivElement"
        class="rounded parent-div"
        :class="[computedElevation, itemStateColor, computedSelectedClass, computedLinkStateClass, computedInspectorClass]"
        :style="computeStyle"
        :data-item-id="props.item.key"
        @pointerdown.left.stop="onCardPointerDown($event)"
        @click="onCardClick($event)"
    >
        <div class="bg-primary title-row">
            <div class="title-text text-caption">
                {{ props.item.label }}
            </div>
            <div class="float-right mr-1">
                <item-menu-button :item="props.item" />
            </div>
            <div v-if="settings.pointAndClickEnabled" class="float-right mr-1">
                <v-btn
                    size="x-small"
                    color="secondary"
                    variant="flat"
                    @pointerdown.left.stop
                    @click.stop="selectItem(SelectedClassType.BlueprintItemModel, props.item)"
                >
                    <v-icon :icon="mdiCursorMove" />
                </v-btn>
            </div>
            <div v-if="blueprintModel.isUpgradeMode && props.item.isUpgradable()" class="float-right mr-1">
                <v-btn
                    size="x-small"
                    color="secondary"
                    variant="flat"
                    rounded="0"
                    :disabled="!props.item.isUpgradable(1)"
                    @pointerdown.left.stop
                    @click.stop="props.item.upgrade(1)"
                >
                    <v-icon :icon="mdiTransferUp" />
                </v-btn>
                <v-btn
                    size="x-small"
                    color="secondary"
                    variant="flat"
                    rounded="0"
                    :disabled="!props.item.isUpgradable(-1)"
                    @pointerdown.left.stop
                    @click.stop="props.item.upgrade(-1)"
                >
                    <v-icon :icon="mdiTransferDown" />
                </v-btn>
            </div>
        </div>
        <div v-if="isVertical" class="main-column">
            <div class="io-strip">
                <template v-for="io in topSide.io()" :key="io.key">
                    <blueprint-single-io
                        :io="io"
                        side="top"
                        layout="strip"
                        @text-update="updateIoRects"
                    />
                </template>
            </div>
            <blueprint-single-factory
                :item="props.item"
                @recipes-menu-activate="(activator: Element) => emit('recipes-menu-activate', props.item, activator)"
            />
            <div class="io-strip">
                <template v-for="io in bottomSide.io()" :key="io.key">
                    <blueprint-single-io
                        :io="io"
                        side="bottom"
                        layout="strip"
                        @text-update="updateIoRects"
                    />
                </template>
            </div>
        </div>
        <div v-else class="main-row">
            <div>
                <template v-for="io in leftSide.io()" :key="io.key">
                    <blueprint-single-io
                        :io="io"
                        side="left"
                        @text-update="updateIoRects"
                    />
                </template>
            </div>
            <blueprint-single-factory
                :item="props.item"
                @recipes-menu-activate="(activator: Element) => emit('recipes-menu-activate', props.item, activator)"
            />
            <div>
                <template v-for="io in rightSide.io()" :key="io.key">
                    <blueprint-single-io
                        :io="io"
                        side="right"
                        @text-update="updateIoRects"
                    />
                </template>
            </div>
        </div>
        <blueprint-single-item-status :item="props.item" />
    </div>
</template>

<style scoped>
.parent-div {
    position: absolute;
    user-select: none;
    touch-action: none;
    /* fork change: uniform card width — 12 grid cells (GRID_SIZE = 25px),
       so every factory reads as the same block on the grid */
    width: 300px;
}
/* fork addition: card currently shown in the inspector */
.inspector-selected {
    box-shadow: 0 0 0 2px #3949ab, 0 0 10px rgba(57, 73, 171, 0.5);
}
/* fork addition: link-drag drop verdict rings (see computedLinkStateClass) */
.link-target-valid {
    box-shadow: 0 0 0 3px #43a047, 0 0 14px rgba(67, 160, 71, 0.65);
}
.link-target-invalid {
    box-shadow: 0 0 0 3px #e53935, 0 0 14px rgba(229, 57, 53, 0.55);
}
.link-target-exists {
    box-shadow: 0 0 0 3px #fb8c00, 0 0 14px rgba(251, 140, 0, 0.55);
}
.link-target-recipe {
    box-shadow: 0 0 0 3px #1e88e5, 0 0 14px rgba(30, 136, 229, 0.55);
}
.title-row {
    display: block;
    position: relative;
    height: 1.7rem;
    overflow: hidden;
}
.main-row {
    flex-wrap: nowrap;
    display: flex;
    flex-direction: row;
    align-items: start;
    /* fork change: pin input ports to the left edge and output ports to
       the right edge of the fixed-width card */
    justify-content: space-between;
    width: 100%;
}
/* fork addition: TB/BT orientations — ports become horizontal strips
   along the top and bottom edges; the factory block sits between them */
.main-column {
    display: flex;
    flex-direction: column;
    width: 100%;
}
.io-strip {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 2px 6px;
    padding: 2px 4px;
    min-height: 30px;
    box-sizing: border-box;
}
</style>
